import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getCreditPackages, createCreditPackage, updateCreditPackage, deleteCreditPackage } from "@/api/admin.api";
import type { CreditPackageType } from "@/api/admin.api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Coins, Plus, Trash2, Save } from "lucide-react";

function PackageForm({ pkg, onDone }: { pkg?: CreditPackageType; onDone: () => void }) {
  const qc = useQueryClient();
  const [credits, setCredits] = useState(pkg?.credits ?? 100);
  const [priceTry, setPriceTry] = useState(pkg?.priceTry ?? 0);
  const [priceUsd, setPriceUsd] = useState(pkg?.priceUsd ?? 0);
  const [isActive, setIsActive] = useState(pkg?.isActive ?? true);
  const [sortOrder, setSortOrder] = useState(pkg?.sortOrder ?? 0);

  const createMut = useMutation({
    mutationFn: () => createCreditPackage({ credits, priceTry, priceUsd, isActive, sortOrder }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-credit-packages"] }); onDone(); },
  });

  const updateMut = useMutation({
    mutationFn: () => updateCreditPackage(pkg!._id, { credits, priceTry, priceUsd, isActive, sortOrder }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-credit-packages"] }),
  });

  const handleSave = () => {
    if (pkg) updateMut.mutate();
    else createMut.mutate();
  };

  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label>Kredi Miktari</Label>
        <Input type="number" min={1} value={credits} onChange={(e) => setCredits(Number(e.target.value))} />
      </div>
      <div>
        <Label>Fiyat (TRY)</Label>
        <Input type="number" min={0} step="0.01" value={priceTry} onChange={(e) => setPriceTry(Number(e.target.value))} />
      </div>
      <div>
        <Label>Fiyat (USD)</Label>
        <Input type="number" min={0} step="0.01" value={priceUsd} onChange={(e) => setPriceUsd(Number(e.target.value))} />
      </div>
      <div>
        <Label>Siralama</Label>
        <Input type="number" value={sortOrder} onChange={(e) => setSortOrder(Number(e.target.value))} />
      </div>
      <div className="col-span-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Switch checked={isActive} onCheckedChange={setIsActive} />
          <Label>{isActive ? "Aktif" : "Pasif"}</Label>
        </div>
        <Button size="sm" onClick={handleSave} disabled={createMut.isPending || updateMut.isPending || credits <= 0 || priceTry <= 0}>
          <Save className="mr-1.5 size-3.5" />
          {(createMut.isPending || updateMut.isPending) ? "Kaydediliyor..." : "Kaydet"}
        </Button>
      </div>
      {(createMut.isSuccess || updateMut.isSuccess) && <p className="col-span-2 text-xs text-green-600">Kaydedildi</p>}
    </div>
  );
}

export function CreditPackagesPage() {
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);

  const { data: packages, isLoading } = useQuery({
    queryKey: ["admin-credit-packages"],
    queryFn: getCreditPackages,
  });

  const delMut = useMutation({
    mutationFn: deleteCreditPackage,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-credit-packages"] }),
  });

  return (
    <div className="max-w-2xl">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Coins className="size-6" />
          Kredi Paketleri
        </h1>
        <Button onClick={() => setShowNew(true)} disabled={showNew}>
          <Plus className="mr-1.5 size-4" />
          Yeni Paket
        </Button>
      </div>

      {showNew && (
        <Card className="mb-4">
          <CardContent className="pt-5">
            <p className="text-sm font-semibold mb-3">Yeni Paket</p>
            <PackageForm onDone={() => setShowNew(false)} />
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <p className="py-8 text-center text-muted-foreground">Yukleniyor...</p>
      ) : !packages?.length ? (
        <p className="py-8 text-center text-muted-foreground">Henuz kredi paketi yok</p>
      ) : (
        <div className="space-y-3">
          {packages.map((pkg) => (
            <Card key={pkg._id}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10">
                      <Coins className="size-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-bold">{pkg.credits} Kredi</p>
                      <p className="text-xs text-muted-foreground">{pkg.priceTry} TRY / ${pkg.priceUsd || 0} USD</p>
                    </div>
                  </div>
                  <ConfirmDialog
                    trigger={<Button variant="ghost" size="icon" className="size-8"><Trash2 className="size-4 text-destructive" /></Button>}
                    title="Paketi Sil"
                    description="Bu kredi paketi kalici olarak silinecek."
                    confirmLabel="Sil"
                    onConfirm={() => delMut.mutate(pkg._id)}
                  />
                </div>
                <PackageForm pkg={pkg} onDone={() => {}} />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
